import { MonthEnum, SelectionTypeEnum } from "enums/enums";
import { currentYear, toIntSafe } from "./utils";
import Const from 'const.json';
import { store } from "./../redux/store";
import { Workbook } from 'exceljs';
import { saveAs } from 'file-saver';
import { useAppSelector } from '../redux/hooks';

export const SelectionChangeHandler = (selectionType: SelectionTypeEnum, SelectionProps: { keys?: MonthEnum | MonthEnum[], onSelectionChange: any; indexes?: number[]; }) => {

  return (selectedString: string, selectedKeys: MonthEnum | MonthEnum[], selectedIndexes: number[]) => {
    SelectionProps.onSelectionChange(selectionType, selectedKeys, selectedString, selectedIndexes);
  };
};

export const monthsMap = [
  [MonthEnum.January, MonthEnum.February, MonthEnum.March],
  [MonthEnum.April, MonthEnum.May, MonthEnum.June],
  [MonthEnum.July, MonthEnum.August, MonthEnum.September],
  [MonthEnum.October, MonthEnum.November, MonthEnum.December],
];

export const setPeriodsForServer = () => {
  const Year = toIntSafe(store.getState().general.period.year.text) ?? currentYear;
  let Months: number[];
  if (store.getState().general.period.quarterly.indexes) {
    Months = store.getState().general.period.quarterly.indexes.flatMap((quarter) => monthsMap[quarter]);
  } else {
    Months = store.getState().general.period.month.indexes?.map(index => index + 1);
  }
  return { Year, Months: Months ?? []};
};

export function useYearFromRedux() {
  const currentYearFromSession = useAppSelector(state => toIntSafe(state.general.period.year.text)) ?? currentYear;
  const lastYearFromSession = currentYearFromSession - 1;
  const comparisonTextToLastYear = `${Const.comparedToTheCorrespondingPeriodIn}${lastYearFromSession}`;
  const comparedOnYear = `${Const.yearOnYearComparison} ${lastYearFromSession}`;

  return {
    currentYearFromSession,
    lastYearFromSession,
    comparisonTextToLastYear,
    comparedOnYear
  };
}

export const handleKeyDownEvent = (event: React.KeyboardEvent<Element>, callback: () => void) => {
  const { key } = event;
  const triggerKeys = ["Enter", " "];
  if (triggerKeys.includes(key)) {
    callback();
  }
};

export const renderPeriod = (
  label: string,
  periodText: string,
  defaultText = ""
) => {
  return periodText ? `${label} ${periodText}` : defaultText;
};

export function exportXlsx(filename: string, headerNames: string[], data: any[], withColors: boolean, dontReplaceRow: boolean) {
  const workBook = new Workbook();
  const workSheet = workBook.addWorksheet(filename);

  // Set the worksheet to RTL
  workSheet.views = [{ rightToLeft: true }];

  // Add header names
  const headerRow = workSheet.addRow(headerNames);
  headerRow.eachCell((cell) => {
    cell.alignment = { horizontal: 'right', readingOrder: 'rtl' };
    cell.font = { bold: true }; // Make the header row bold
  });

  // Process each row of data
  data.forEach(itemValuesArr => {
    const row = workSheet.addRow(itemValuesArr);
    row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
      if (typeof cell.value === 'number') {
        if (Number.isInteger(cell.value)) {
          cell.numFmt = '#,##0'; // Format number with commas
        }
        else {
          cell.numFmt = '#,##0.00'; // Format number with commas
        }
      }
    });
  });

  // Make the first column bold
  workSheet.eachRow((row, rowNumber) => {
    const cell = row.getCell(1);
    cell.font = { bold: true };
  });

  // Adjust column widths to fit content
  workSheet.columns.forEach((column) => {
    let maxLength = 10; // Set a default minimum width
    column.eachCell({ includeEmpty: true }, (cell) => {
      if (cell.value) {
        const cellValueLength = cell.value.toString().length;
        maxLength = Math.max(maxLength, cellValueLength);
      }
    });
    column.width = maxLength;
  });

  // Generate the Excel file and trigger the download
  workBook.xlsx.writeBuffer().then(buffer => {
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, `${filename}.xlsx`);
  });
}

